import { useConfigurator } from '@/app/store/configurator/hooks/use-configurator';
import {
	BORDER_WIDTH,
	RACK_BORDER_MATERIAL_COLORS,
	SHELF_DECK,
	SHELVE_MATERIAL_COLORS,
	SHELVE_MATERIALS_LIMITS,
} from '@/shared/constants/configurator';
import { DISPLAY_SCALE } from '@/shared/constants/editor';
import { ShelfDeck } from '@/shared/types/configurator';
import { Expanded } from '@/shared/ui/expanded/expanded';
import { useExpandedContext } from '@/shared/ui/expanded/hooks/use-expanded-context';
import { Select } from '@/shared/ui/select/select';
import { PencilIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import styles from './front-shelf.module.css';
import { FrontShelfProps } from './front-shelf.props';

export const FrontShelf = ({ shelf, sectionIndex, shelfIndex, ...props }: FrontShelfProps) => {
	const {
		state: { material },
	} = useConfigurator();

	const shelfBorderWidth = (BORDER_WIDTH * DISPLAY_SCALE) / 3;

	return (
		<div {...props}>
			<div className={styles.wrapper}>
				<div
					className={styles.shelf}
					style={{
						marginLeft: BORDER_WIDTH * DISPLAY_SCALE,
					}}
				>
					<div
						style={{ backgroundColor: SHELVE_MATERIAL_COLORS[shelf.deck] }}
						className={styles.coating}
					/>
					<div
						style={{
							backgroundColor: RACK_BORDER_MATERIAL_COLORS[material],
							width: `calc(100% + ${shelfBorderWidth * 2}px)`,
							transform: `translateX(-${shelfBorderWidth}px)`,
						}}
						className={styles.rackMaterialLarge}
					/>
					<div
						style={{
							backgroundColor: RACK_BORDER_MATERIAL_COLORS[material],
						}}
						className={styles.rackMaterialSmall}
					/>
					<div
						style={{
							backgroundColor: RACK_BORDER_MATERIAL_COLORS[material],
							width: `calc(100% + ${shelfBorderWidth * 2}px)`,
							transform: `translateX(-${shelfBorderWidth}px)`,
						}}
						className={styles.rackMaterialLarge}
					/>
				</div>
				<Expanded
					classNames={{
						wrapper: styles.expandedWrapper,
						content: styles.expandedContent,
					}}
					triggerContent={
						<button className={styles.editButton}>
							<PencilIcon />
						</button>
					}
					content={
						<FrontShelfEditor
							shelf={shelf}
							sectionIndex={sectionIndex}
							shelfIndex={shelfIndex}
						/>
					}
				/>
			</div>
		</div>
	);
};

const FrontShelfEditor = ({ shelf, sectionIndex, shelfIndex }: FrontShelfProps) => {
	const { t } = useTranslation();
	const { setIsOpen } = useExpandedContext();
	const {
		state: { material, sections },
		updateSections,
	} = useConfigurator();

	const displayShelfDeckOptions = SHELF_DECK.reduce((acc, option) => {
		if (SHELVE_MATERIALS_LIMITS[material].includes(option.value)) {
			acc.push({
				...option,
				label: t(option.label),
			});
		}

		return acc;
	}, []);

	const handleUpdateShelf = (deck: ShelfDeck) => {
		const currentSection = sections[sectionIndex];
		if (!currentSection) {
			return;
		}

		const updatedSection = {
			...currentSection,
			selfs: currentSection.selfs.map((shelf, index) => {
				if (index === shelfIndex) {
					return {
						...shelf,
						deck,
					};
				}

				return shelf;
			}),
		};

		updateSections([sectionIndex], updatedSection);
		setIsOpen(false);
	};

	return (
		<div className={styles.editorWrapper}>
			<Select
				value={shelf.deck}
				options={displayShelfDeckOptions}
				label={t('material.shelf.title')}
				onChange={(deck) => handleUpdateShelf(deck as ShelfDeck)}
			/>
		</div>
	);
};
